import { useEffect, useMemo, useRef, useState } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Pagination } from "swiper/modules";
import { ChevronLeft, ChevronRight, Maximize2 } from "lucide-react";
import type { Swiper as SwiperType } from "swiper";

import "swiper/css";
import "swiper/css/pagination";

import styles from "./ImageCarousel.module.css";
import CloseButton from "../Button/CloseButton/CloseButton";
import { handleBackgroundScroll } from "../../utils/backgroundScroll";
import { PRODUCT_PLACEHOLDER_IMAGE } from "../../services/productImageService";

interface ImageCarouselProps {
  images: string[];   
  className?: string;
  onPopup?: boolean;
}

const ImageCarousel = ({
  images,
  className,
  onPopup = false,
}: ImageCarouselProps) => {
  const [expanded, setExpanded] = useState(false);
  const swiperRef = useRef<SwiperType | null>(null);
  const validImages = useMemo(
    () =>
      images
        .map((src) => (typeof src === "string" ? src.trim() : ""))
        .filter((src): src is string => src.length > 0),
    [images]
  );

  useEffect(() => {
    if (onPopup || !expanded) return;

    handleBackgroundScroll(true);
    return () => {
      handleBackgroundScroll(false);
    };
  }, [expanded, onPopup]);

  if (validImages.length === 0) return null;

  const showNavigation = validImages.length > 1;

  return (
    <div
      className={`${styles.GalleryPhoto} ${className ?? ""} ${
        expanded ? styles.Expanded : ""
      }`}
    >
      <Swiper
        modules={[Pagination]}
        slidesPerView={1}
        pagination={{ clickable: true }}
        loop={validImages.length > 1}
        onSwiper={(swiper: SwiperType) => {
          swiperRef.current = swiper;
        }}
        className={`${styles.Carousel} `}
      >
        {validImages.map((src, index) => (
          <SwiperSlide key={index}>
            <img
              className={styles.SlideBg}
              src={src}
              alt=""
              loading={index === 0 ? "eager" : "lazy"}
              onError={(event) => {
                if (event.currentTarget.dataset.placeholderApplied === "true") return;
                event.currentTarget.dataset.placeholderApplied = "true";
                event.currentTarget.src = PRODUCT_PLACEHOLDER_IMAGE;
              }}
            />
          </SwiperSlide>
        ))}
      </Swiper>

      {showNavigation && (
        <>
          <button
            type="button"
            className={`${styles.NavButton} ${styles.PrevButton}`}
            aria-label="Previous image"
            onClick={() => swiperRef.current?.slidePrev()}
          >
            <ChevronLeft />
          </button>
          <button
            type="button"
            className={`${styles.NavButton} ${styles.NextButton}`}
            aria-label="Next image"
            onClick={() => swiperRef.current?.slideNext()}
          >
            <ChevronRight />
          </button>
        </>
      )}

      {expanded ? (
        <CloseButton
          onClick={() => {
            setExpanded(false);
            if (!onPopup) handleBackgroundScroll(false);
          }}
          className={styles.Close}
        />
      ) : (
        <button
          className={styles.ExpandButton}
          onClick={() => setExpanded(true)}
        >
          <Maximize2 />
        </button>
      )}
    </div>
  );
};

export default ImageCarousel;
